import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Typography,
  Box,
  Stack,
  Chip,
  CircularProgress,
  Button as MuiButton,
  Divider,
} from '@mui/material';
import { CloseIcon } from '../../../helper/Icons';
import { FaFileCsv, FaFileExcel, FaColumns, FaListAlt } from 'react-icons/fa';
import { saveAs } from 'file-saver';
import { useDispatch } from 'react-redux';
import { notificationSuccess } from '../../../state/Action/NotificationAction';
import { exportForm, exportFormV2, exportFormExcel } from '../../../Service/form.service';

const ExportModal = ({ open, onClose, formId, formTitle, submissionCount }) => {
  const dispatch = useDispatch();
  const [loadingOption, setLoadingOption] = useState(null);

  const cleanFileName = (name, ext) => {
    const base = (name || 'FormData').replace(/[^a-zA-Z0-9_-]/g, '_');
    return `${base}_${Date.now()}.${ext}`;
  };

  // Helper for Legacy CSV Export
  const handleLegacyExport = async () => {
    setLoadingOption('legacy');
    try {
      const res = await exportForm(formId);
      if (res?.status) {
        const getQuestionText = (q) => {
          if (q === null || q === undefined) return '';
          let text = '';
          if (typeof q === 'object') {
            text = q.question || q.title || q.name || JSON.stringify(q);
          } else {
            text = String(q);
          }
          return text.replace(/\r?\n/g, ' ');
        };

        const getAnswerText = (value) => {
          if (value === null || value === undefined || value === '') return '';
          if (typeof value === 'object') {
            if (Array.isArray(value)) {
              return value
                .map((val) => (typeof val === 'object' ? getAnswerText(val) : String(val)))
                .join(', ')
                .replace(/\r?\n/g, ' ');
            }
            if (value.matrixAnswers && Array.isArray(value.matrixAnswers)) {
              const rows = value.matrixAnswers
                .slice(1)
                .map((row) => {
                  if (Array.isArray(row)) {
                    return row
                      .map((cell) => cell?.value || cell?.answer || '')
                      .filter((v) => v !== '')
                      .join(': ');
                  }
                  return '';
                })
                .filter(Boolean);
              return rows.join(', ').replace(/\r?\n/g, ' ');
            }
            if (value.filesAnswers && Array.isArray(value.filesAnswers)) {
              const paths = value.filesAnswers
                .map((file) => file.serverPath || file.localPath || '')
                .filter(Boolean);
              if (paths.length > 0) {
                const mediaStr = paths.join(', ');
                if (value.answer) {
                  return `${value.answer} (Media: ${mediaStr})`.replace(/\r?\n/g, ' ');
                }
                return mediaStr.replace(/\r?\n/g, ' ');
              }
            }
            let parts = [];
            if (value.answer) parts.push(value.answer);
            if (value.answers) {
              if (Array.isArray(value.answers)) {
                parts.push(value.answers.join(', '));
              } else {
                parts.push(value.answers);
              }
            }
            if (value.audioUrl) parts.push(`(Audio: ${value.audioUrl})`);
            if (parts.length > 0) return parts.join(', ').replace(/\r?\n/g, ' ');
            return JSON.stringify(value).replace(/\r?\n/g, ' ');
          }
          return String(value).replace(/\r?\n/g, ' ');
        };

        const questions = res?.data.questions || [];
        const rawValues = res?.data.values || [];
        const headerRow = questions.map((q) => getQuestionText(q));
        const alignedRows = rawValues.map((row) => row.map((val) => getAnswerText(val)));
        const data = [headerRow, ...alignedRows];

        const csvContent = data
          .map((row) =>
            row.map((value) => `"${String(value).replace(/"/g, '""')}"`).join(',')
          )
          .join('\n');

        const blob = new Blob(['\uFEFF' + csvContent], {
          type: 'text/csv;charset=utf-8;',
        });
        saveAs(blob, cleanFileName(`${formTitle || 'Form'}_Default_Old`, 'csv'));
        dispatch(
          notificationSuccess({
            show: true,
            type: 'success',
            message: 'Default CSV exported successfully',
          })
        );
      } else {
        dispatch(
          notificationSuccess({
            show: true,
            type: 'error',
            message: res?.message || 'Failed to export default CSV',
          })
        );
      }
    } catch (err) {
      console.error(err);
      dispatch(
        notificationSuccess({
          show: true,
          type: 'error',
          message: 'Error downloading default CSV',
        })
      );
    } finally {
      setLoadingOption(null);
    }
  };

  // Helper for V2 CSV Exports (Columns or Summary)
  const handleV2Export = async (mode) => {
    setLoadingOption(mode);
    try {
      const res = await exportFormV2(formId, { matrixMode: mode });
      if (res?.status) {
        const questions = res?.data?.questions || [];
        const rawValues = res?.data?.values || [];
        const rows = [questions, ...rawValues];

        const csvContent = rows
          .map((row) =>
            (row || [])
              .map((cell) => `"${String(cell ?? '').replace(/"/g, '""')}"`)
              .join(',')
          )
          .join('\n');

        const blob = new Blob(['\uFEFF' + csvContent], {
          type: 'text/csv;charset=utf-8;',
        });

        const suffix = mode === 'columns' ? 'Option1_Columns' : 'Option2_Summary';
        saveAs(blob, cleanFileName(`${formTitle || 'Form'}_${suffix}`, 'csv'));
        dispatch(
          notificationSuccess({
            show: true,
            type: 'success',
            message: `Export (${mode === 'columns' ? 'Option 1: Columns' : 'Option 2: Summary'}) downloaded successfully`,
          })
        );
      } else {
        dispatch(
          notificationSuccess({
            show: true,
            type: 'error',
            message: res?.message || 'Failed to export CSV',
          })
        );
      }
    } catch (err) {
      console.error(err);
      dispatch(
        notificationSuccess({
          show: true,
          type: 'error',
          message: 'Error downloading CSV',
        })
      );
    } finally {
      setLoadingOption(null);
    }
  };

  // Helper for Multi-Sheet Excel Export
  const handleExcelExport = async () => {
    setLoadingOption('excel');
    try {
      const res = await exportFormExcel(formId);
      if (res?.status) {
        saveAs(res.data, cleanFileName(`${formTitle || 'Form'}_Option3_MultiSheet`, 'xlsx'));
        dispatch(
          notificationSuccess({
            show: true,
            type: 'success',
            message: 'Multi-Sheet Excel exported successfully',
          })
        );
      } else {
        dispatch(
          notificationSuccess({
            show: true,
            type: 'error',
            message: res?.message || 'Failed to export Excel file',
          })
        );
      }
    } catch (err) {
      console.error(err);
      dispatch(
        notificationSuccess({
          show: true,
          type: 'error',
          message: 'Error downloading Excel file',
        })
      );
    } finally {
      setLoadingOption(null);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={loadingOption ? undefined : onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '16px',
          boxShadow: '0 20px 40px rgba(0,0,0,0.15)',
          overflow: 'hidden',
        },
      }}
    >
      <DialogTitle
        sx={{
          m: 0,
          p: 2.5,
          bgcolor: '#f8fafc',
          borderBottom: '1px solid #e2e8f0',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 700, color: '#1e293b' }}>
            Export Survey Data
          </Typography>
          <Stack direction="row" spacing={1} alignItems="center" mt={0.5}>
            <Typography variant="body2" sx={{ color: '#64748b' }}>
              Form: <strong>{formTitle || 'Survey Form'}</strong>
            </Typography>
            {submissionCount !== undefined && (
              <Chip
                label={`${submissionCount} submissions`}
                size="small"
                sx={{
                  bgcolor: '#e0e7ff',
                  color: '#4338ca',
                  fontWeight: 600,
                  fontSize: '11px',
                }}
              />
            )}
          </Stack>
        </Box>
        <IconButton
          aria-label="close"
          onClick={onClose}
          disabled={Boolean(loadingOption)}
          sx={{ color: '#94a3b8', '&:hover': { color: '#475569' } }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 3, bgcolor: '#ffffff' }}>
        <Typography variant="subtitle2" sx={{ color: '#475569', mb: 2.5 }}>
          Select an export format below to download your survey data:
        </Typography>

        <Stack spacing={2}>
          {/* OPTION 1: Default / Old Export */}
          <Box
            sx={{
              p: 2,
              borderRadius: '12px',
              border: '1px solid #e2e8f0',
              bgcolor: '#f8fafc',
              transition: 'all 0.2s ease',
              '&:hover': { borderColor: '#cbd5e1', boxShadow: '0 4px 12px rgba(0,0,0,0.04)' },
            }}
          >
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              justifyContent="space-between"
              alignItems={{ xs: 'flex-start', sm: 'center' }}
              gap={2}
            >
              <Box sx={{ flex: 1 }}>
                <Stack direction="row" spacing={1} alignItems="center" mb={0.5}>
                  <FaFileCsv size={20} color="#64748b" />
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#1e293b' }}>
                    Default (Old Export)
                  </Typography>
                  <Chip label="Legacy" size="small" sx={{ bgcolor: '#e2e8f0', color: '#475569', fontSize: '11px' }} />
                </Stack>
                <Typography variant="body2" sx={{ color: '#64748b', fontSize: '13px' }}>
                  Standard legacy CSV export without any format modifications.
                </Typography>
              </Box>
              <MuiButton
                variant="outlined"
                onClick={handleLegacyExport}
                disabled={Boolean(loadingOption)}
                sx={{
                  minWidth: '220px',
                  textTransform: 'none',
                  fontWeight: 600,
                  borderColor: '#94a3b8',
                  color: '#475569',
                  '&:hover': { borderColor: '#64748b', bgcolor: '#f1f5f9' },
                }}
              >
                {loadingOption === 'legacy' ? (
                  <>
                    <CircularProgress size={16} sx={{ mr: 1, color: '#475569' }} />
                    Exporting...
                  </>
                ) : (
                  'Download Default CSV'
                )}
              </MuiButton>
            </Stack>
          </Box>

          {/* OPTION 2: Option 1 (Wide Columns CSV) */}
          <Box
            sx={{
              p: 2,
              borderRadius: '12px',
              border: '1.5px solid #bfdbfe',
              bgcolor: '#eff6ff',
              transition: 'all 0.2s ease',
              '&:hover': { borderColor: '#93c5fd', boxShadow: '0 4px 12px rgba(59,130,246,0.08)' },
            }}
          >
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              justifyContent="space-between"
              alignItems={{ xs: 'flex-start', sm: 'center' }}
              gap={2}
            >
              <Box sx={{ flex: 1 }}>
                <Stack direction="row" spacing={1} alignItems="center" mb={0.5}>
                  <FaColumns size={20} color="#2563eb" />
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#1e40af' }}>
                    Option 1: Wide Columns (CSV)
                  </Typography>
                  <Chip
                    label="Hierarchical"
                    size="small"
                    sx={{ bgcolor: '#dbeafe', color: '#1e40af', fontWeight: 600, fontSize: '11px' }}
                  />
                </Stack>
                <Typography variant="body2" sx={{ color: '#3b82f6', fontSize: '13px' }}>
                  All Matrix questions split into individual columns with full metadata (Result ID, Outlet, Local Time, Duration, Auditor Info, GPS, Submitted At).
                </Typography>
              </Box>
              <MuiButton
                variant="contained"
                onClick={() => handleV2Export('columns')}
                disabled={Boolean(loadingOption)}
                sx={{
                  minWidth: '220px',
                  textTransform: 'none',
                  fontWeight: 600,
                  bgcolor: '#2563eb',
                  color: '#ffffff',
                  '&:hover': { bgcolor: '#1d4ed8' },
                }}
              >
                {loadingOption === 'columns' ? (
                  <>
                    <CircularProgress size={16} sx={{ mr: 1, color: '#ffffff' }} />
                    Exporting...
                  </>
                ) : (
                  'Download Wide Columns (CSV)'
                )}
              </MuiButton>
            </Stack>
          </Box>

          {/* OPTION 3: Option 2 (Compact Summary CSV) */}
          <Box
            sx={{
              p: 2,
              borderRadius: '12px',
              border: '1.5px solid #fde68a',
              bgcolor: '#fffbeb',
              transition: 'all 0.2s ease',
              '&:hover': { borderColor: '#fcd34d', boxShadow: '0 4px 12px rgba(245,158,11,0.08)' },
            }}
          >
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              justifyContent="space-between"
              alignItems={{ xs: 'flex-start', sm: 'center' }}
              gap={2}
            >
              <Box sx={{ flex: 1 }}>
                <Stack direction="row" spacing={1} alignItems="center" mb={0.5}>
                  <FaListAlt size={20} color="#d97706" />
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#92400e' }}>
                    Option 2: Compact Summary (CSV)
                  </Typography>
                  <Chip
                    label="Single Column"
                    size="small"
                    sx={{ bgcolor: '#fef3c7', color: '#92400e', fontWeight: 600, fontSize: '11px' }}
                  />
                </Stack>
                <Typography variant="body2" sx={{ color: '#b45309', fontSize: '13px' }}>
                  All Matrix responses consolidated into a single summary column along with full metadata fields.
                </Typography>
              </Box>
              <MuiButton
                variant="contained"
                onClick={() => handleV2Export('summary')}
                disabled={Boolean(loadingOption)}
                sx={{
                  minWidth: '220px',
                  textTransform: 'none',
                  fontWeight: 600,
                  bgcolor: '#d97706',
                  color: '#ffffff',
                  '&:hover': { bgcolor: '#b45309' },
                }}
              >
                {loadingOption === 'summary' ? (
                  <>
                    <CircularProgress size={16} sx={{ mr: 1, color: '#ffffff' }} />
                    Exporting...
                  </>
                ) : (
                  'Download Summary (CSV)'
                )}
              </MuiButton>
            </Stack>
          </Box>

          {/* OPTION 4: Option 3 (Multi-Sheet Excel .xlsx) */}
          <Box
            sx={{
              p: 2,
              borderRadius: '12px',
              border: '1.5px solid #a7f3d0',
              bgcolor: '#ecfdf5',
              transition: 'all 0.2s ease',
              '&:hover': { borderColor: '#6ee7b7', boxShadow: '0 4px 12px rgba(16,185,129,0.08)' },
            }}
          >
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              justifyContent="space-between"
              alignItems={{ xs: 'flex-start', sm: 'center' }}
              gap={2}
            >
              <Box sx={{ flex: 1 }}>
                <Stack direction="row" spacing={1} alignItems="center" mb={0.5}>
                  <FaFileExcel size={20} color="#059669" />
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#065f46' }}>
                    Option 3: Multi-Sheet Excel (.xlsx)
                  </Typography>
                  <Chip
                    label="Recommended for Matrix"
                    size="small"
                    sx={{ bgcolor: '#d1fae5', color: '#065f46', fontWeight: 600, fontSize: '11px' }}
                  />
                </Stack>
                <Typography variant="body2" sx={{ color: '#047857', fontSize: '13px' }}>
                  Sheet 1 (Survey Responses) + Sheet 2 (Matrix Transactions). Best for pivot tables, detailed analysis, and reporting.
                </Typography>
              </Box>
              <MuiButton
                variant="contained"
                onClick={handleExcelExport}
                disabled={Boolean(loadingOption)}
                sx={{
                  minWidth: '220px',
                  textTransform: 'none',
                  fontWeight: 600,
                  bgcolor: '#059669',
                  color: '#ffffff',
                  '&:hover': { bgcolor: '#047857' },
                }}
              >
                {loadingOption === 'excel' ? (
                  <>
                    <CircularProgress size={16} sx={{ mr: 1, color: '#ffffff' }} />
                    Exporting...
                  </>
                ) : (
                  'Download Excel (.xlsx)'
                )}
              </MuiButton>
            </Stack>
          </Box>
        </Stack>
      </DialogContent>
    </Dialog>
  );
};

export default ExportModal;
